import React, { useEffect, useState } from "react";
import { Card, Table, Row, Col, Button, Form, Alert } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { getEmployeeStatement, submitExerciseRequest, getStockPrices } from "../../../apis/options/EmployeeApi";
import Dollars from "../../common/typeFormatting/Dollars";
import IntegerFmt from "../../common/typeFormatting/IntegerFmt";
import DateFmt from "../../common/typeFormatting/Date";

const ExerciseOptionsPage = () => {
    const { employeeId } = useParams();
    const history = useHistory();
    const [empStmt, setEmpStmt] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [cashlessQuantities, setCashlessQuantities] = useState({});
    const [purchaseQuantities, setPurchaseQuantities] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [requestDate, setRequestDate] = useState(new Date().toISOString().split('T')[0]); // Today's date in YYYY-MM-DD format
    const [stockPrices, setStockPrices] = useState([]);
    const [selectedStockPrice, setSelectedStockPrice] = useState(null);

    useEffect(() => {
        async function loadEmployeeStatement() {
            try {
                const stmt = await getEmployeeStatement(employeeId);
                setEmpStmt(stmt);
                
                // Initialize exercise quantities to 0 for each option
                const initialQuantities = {};
                stmt.options.forEach(option => {
                    const vestedRemaining = option.vestedShares - (option.exercisedShares || 0);
                    if (vestedRemaining > 0) {
                        initialQuantities[option.optionAgreementId] = 0;
                    }
                });
                setCashlessQuantities(initialQuantities);
                setPurchaseQuantities(initialQuantities);
                setIsLoading(false);
            } catch (error) {
                console.error("Error loading employee statement:", error);
                setIsLoading(false);
            }
        }

        async function loadStockPrices() {
            try {
                const prices = await getStockPrices();
                setStockPrices(prices);
                
                // Find the stock price for today's date
                const today = new Date(requestDate);
                today.setHours(0, 0, 0, 0); // Reset time to start of day
                
                const priceForToday = prices.find(price => {
                    const start = price.effectiveStart ? new Date(price.effectiveStart) : null;
                    const end = price.effectiveEnd ? new Date(price.effectiveEnd) : null;
                    
                    if (start) start.setHours(0, 0, 0, 0);
                    if (end) end.setHours(23, 59, 59, 999);
                    
                    if (start && end) {
                        return today >= start && today <= end;
                    } else if (start && !end) {
                        return today >= start;
                    } else if (price.isCurrent) {
                        return true;
                    }
                    return false;
                });
                
                setSelectedStockPrice(priceForToday);
            } catch (error) {
                console.error("Error loading stock prices:", error);
            }
        }

        if (employeeId) {
            loadEmployeeStatement();
            loadStockPrices();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeId]);

    const handleCashlessQuantityChange = (optionAgreementId, value) => {
        const numValue = parseInt(value) || 0;
        setCashlessQuantities(prev => ({
            ...prev,
            [optionAgreementId]: numValue
        }));
    };

    const handlePurchaseQuantityChange = (optionAgreementId, value) => {
        const numValue = parseInt(value) || 0;
        setPurchaseQuantities(prev => ({
            ...prev,
            [optionAgreementId]: numValue
        }));
    };

    const handleCashlessAll = (optionAgreementId, maxQuantity) => {
        setCashlessQuantities(prev => ({
            ...prev,
            [optionAgreementId]: maxQuantity
        }));
        setPurchaseQuantities(prev => ({
            ...prev,
            [optionAgreementId]: 0
        }));
    };

    const handlePurchaseAll = (optionAgreementId, maxQuantity) => {
        setPurchaseQuantities(prev => ({
            ...prev,
            [optionAgreementId]: maxQuantity
        }));
        setCashlessQuantities(prev => ({
            ...prev,
            [optionAgreementId]: 0
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setSubmitError("");
        
        try {
            // Filter out options with 0 quantity
            const exerciseRequests = [];

            // Add cashless exercise requests
            Object.entries(cashlessQuantities)
                .filter(([_, quantity]) => quantity > 0)
                .forEach(([optionAgreementId, quantity]) => {
                    exerciseRequests.push({
                        optionAgreementId: parseInt(optionAgreementId),
                        cashlessQuantity: quantity,
                        purchaseQuantity: 0
                    });
                });

            // Add purchase exercise requests
            Object.entries(purchaseQuantities)
                .filter(([_, quantity]) => quantity > 0)
                .forEach(([optionAgreementId, quantity]) => {
                    const existingRequest = exerciseRequests.find(req => req.optionAgreementId === parseInt(optionAgreementId));
                    if (existingRequest) {
                        existingRequest.purchaseQuantity = quantity;
                    } else {
                        exerciseRequests.push({
                            optionAgreementId: parseInt(optionAgreementId),
                            cashlessQuantity: 0,
                            purchaseQuantity: quantity
                        });
                    }
                });

            if (exerciseRequests.length === 0) {
                setSubmitError("Please specify at least one option to exercise.");
                setIsSubmitting(false);
                return;
            }

            // Submit exercise request with request date
            const exerciseRequest = await submitExerciseRequest(employeeId, exerciseRequests, `Exercise request for ${requestDate}`, requestDate);
            
            // Navigate to exercise request details page
            history.push(`/options/exercise-requests/${exerciseRequest.exerciseRequestId}`);
            
        } catch (error) {
            console.error("Error exercising options:", error);
            setSubmitError("An error occurred while exercising options. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const getTotalExerciseValue = () => {
        if (!empStmt?.options || !selectedStockPrice) return 0;

        return empStmt.options.reduce((total, option) => {
            const cashlessQty = cashlessQuantities[option.optionAgreementId] || 0;
            const purchaseQty = purchaseQuantities[option.optionAgreementId] || 0;

            const proceeds = cashlessQty * Math.max(0, selectedStockPrice.price - option.strikePrice);
            const purchaseCost = purchaseQty * option.strikePrice;
            const netAmount = proceeds - purchaseCost;

            return total + netAmount;
        }, 0);
    };

    const handleRequestDateChange = (newDate) => {
        setRequestDate(newDate);
        
        // Find the stock price for the selected date
        const selectedDate = new Date(newDate);
        selectedDate.setHours(0, 0, 0, 0); // Reset time to start of day
        
        const priceForDate = stockPrices.find(price => {
            const start = price.effectiveStart ? new Date(price.effectiveStart) : null;
            const end = price.effectiveEnd ? new Date(price.effectiveEnd) : null;
            
            if (start) start.setHours(0, 0, 0, 0);
            if (end) end.setHours(23, 59, 59, 999);
            
            if (start && end) {
                return selectedDate >= start && selectedDate <= end;
            } else if (start && !end) {
                return selectedDate >= start;
            } else if (price.isCurrent) {
                return true;
            }
            return false;
        });
        
        setSelectedStockPrice(priceForDate);
    };

    if (isLoading) {
        return <Row><Col>Loading...</Col></Row>;
    }

    if (!empStmt) {
        return <Row><Col>Employee not found.</Col></Row>;
    }

    const exercisableOptions = empStmt.options.filter(option => {
        const vestedRemaining = option.vestedShares - (option.exercisedShares || 0);
        return vestedRemaining > 0;
    });

    return (
        <Row>
            <Col>
                <Card style={{marginTop: 25}}>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Card.Title>
                                Exercise Options - {empStmt.firstName} {empStmt.lastName}
                            </Card.Title>
                            <Button 
                                variant="secondary" 
                                onClick={() => history.push(`/options/employees/${employeeId}`)}
                            >
                                Back to Employee Statement
                            </Button>
                        </div>

                        {submitError && (
                            <Alert variant="danger">
                                {submitError}
                            </Alert>
                        )}

                        <div className="row mb-4">
                            <div className="col-md-4">
                                <Form.Group>
                                    <Form.Label>Request Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={requestDate}
                                        onChange={(e) => handleRequestDateChange(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4 text-center">
                                <div className="text-muted mb-2">Stock Price on Date</div>
                                <h4><Dollars>{selectedStockPrice?.price || 0}</Dollars></h4>
                            </div>
                            <div className="col-md-4 text-center">
                                <div className="text-muted mb-2">Total Net Amount</div>
                                <h4><Dollars>{getTotalExerciseValue()}</Dollars></h4>
                            </div>
                        </div>

                        {exercisableOptions.length === 0 ? (
                            <Alert variant="info">
                                No exercisable options available for this employee.
                            </Alert>
                        ) : (
                            <>
                                <Table hover responsive style={{fontSize: 14}}>
                                    <thead>
                                        <tr>
                                            <th>Issued</th>
                                            <th>Description</th>
                                            <th className="text-center">Original Issued</th>
                                            <th className="text-center">Vested</th>
                                            <th className="text-center">Exercised</th>
                                            <th className="text-center">Vested Remaining</th>
                                            <th className="text-center">Strike Price</th>
                                            <th className="text-center">Price Difference</th>
                                            <th className="text-center">Cashless Exercise</th>
                                            <th className="text-center">Purchase Exercise</th>
                                            <th className="text-center">Proceeds</th>
                                            <th className="text-center">Purchase Cost</th>
                                            <th className="text-center">Net Amount</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exercisableOptions.map(option => {
                                            const vestedRemaining = option.vestedShares - (option.exercisedShares || 0);
                                            const cashlessQty = cashlessQuantities[option.optionAgreementId] || 0;
                                            const purchaseQty = purchaseQuantities[option.optionAgreementId] || 0;

                                            return (
                                                <tr key={option.optionAgreementId}>
                                                    <td><DateFmt>{option.issueDate}</DateFmt></td>
                                                    <td>{option.description}</td>
                                                    <td className="text-center"><IntegerFmt>{option.totalShares}</IntegerFmt></td>
                                                    <td className="text-center"><IntegerFmt>{option.vestedShares}</IntegerFmt></td>
                                                    <td className="text-center"><IntegerFmt>{option.exercisedShares || 0}</IntegerFmt></td>
                                                    <td className="text-center" style={{backgroundColor: "rgb(243,243,243,1)"}}>
                                                        <IntegerFmt>{vestedRemaining}</IntegerFmt>
                                                    </td>
                                                    <td className="text-center"><Dollars>{option.strikePrice}</Dollars></td>
                                                    <td className="text-center"><Dollars>{selectedStockPrice ? Math.max(0, selectedStockPrice.price - option.strikePrice) : 0}</Dollars></td>
                                                    <td className="text-center">
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            max={Math.max(0, vestedRemaining - purchaseQty)}
                                                            value={cashlessQty}
                                                            onChange={(e) => handleCashlessQuantityChange(option.optionAgreementId, e.target.value)}
                                                            style={{width: "80px", margin: "0 auto"}}
                                                            disabled={isSubmitting}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            max={Math.max(0, vestedRemaining - cashlessQty)}
                                                            value={purchaseQty}
                                                            onChange={(e) => handlePurchaseQuantityChange(option.optionAgreementId, e.target.value)}
                                                            style={{width: "80px", margin: "0 auto"}}
                                                            disabled={isSubmitting}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Dollars>{cashlessQty * Math.max(0, (selectedStockPrice?.price || 0) - option.strikePrice)}</Dollars>
                                                    </td>
                                                    <td className="text-center">
                                                        <Dollars>{purchaseQty * option.strikePrice}</Dollars>
                                                    </td>
                                                    <td className="text-center">
                                                        <Dollars>{(cashlessQty * Math.max(0, (selectedStockPrice?.price || 0) - option.strikePrice)) - (purchaseQty * option.strikePrice)}</Dollars>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleCashlessAll(option.optionAgreementId, vestedRemaining)}
                                                                disabled={isSubmitting}
                                                                title="Exercise all as cashless"
                                                            >
                                                                C-All
                                                            </Button>
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handlePurchaseAll(option.optionAgreementId, vestedRemaining)}
                                                                disabled={isSubmitting}
                                                                title="Exercise all as purchase"
                                                            >
                                                                P-All
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>

                                <div className="d-flex justify-content-end mt-4">
                                    <Button
                                        variant="success"
                                        size="lg"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || (Object.values(cashlessQuantities).every(q => q === 0) && Object.values(purchaseQuantities).every(q => q === 0))}
                                    >
                                        {isSubmitting ? "Processing..." : "Submit"}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default ExerciseOptionsPage;